import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import UserSearch from '@components/UserSearch'

import generateAdCopyReviewerUrl from '@functions/adCopyReviewer'
import generateMediaReviewerUrl from '@functions/mediaReviewer'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCampaign from '@hooks/useCampaign'

import { formatEntityCount } from '@functions/entity'

import SelectedRecipient from './components/SelectedRecipient'
import useShareProjectModal from './hooks/useShareProjectModal'

const MODAL_KEY = 'ShareProjectModal'

export type ModalCallbacks = {
  closeModal: () => void,
  shareProject: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  isAdCopy?: boolean,
  showModal: boolean,
}

const ShareProjectModal = (props: ModalProps) => {
  const {
    callbacks, isAdCopy = false, showModal,
  } = props
  const { closeModal, shareProject } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaign, orderItem, provider } = modalPayload

  const campaignPayload = useCampaign(campaign, { generateUrlsOptions: provider })
  const {
    callbacks: {
      reviewAdCopy,
      reviewMedia,
      shareProject: shareFn,
    },
  } = campaignPayload

  const hookPayload = useShareProjectModal()
  const {
    callbacks: {
      setModalState,
      toggleUser,
    },
    filterString,
    hasSelectedUsers,
    message,
    selectedUsers,
    selectedUsersCount,
  } = hookPayload

  const link = !isAdCopy ? generateMediaReviewerUrl({ campaignId: campaign.id, providerId: provider.id })
    : generateAdCopyReviewerUrl({ campaignId: campaign.id, orderItemId: orderItem?.id, providerId: provider.id })

  const shareProjectPayload = {
    callbacks: {
      action: shareFn,
      afterAction: closeModal,
    },
    entityParams: {
      link,
      message,
      user_ids: selectedUsers.map(user => user.id),
    },
    shouldRedirect: true,
    toastText: 'Project Shared',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Send" titleSecondLine="Email" />

      <SidebarModal.Content>
        <Form.Field label="Search Recipient">
          {/*
           // @ts-expect-error TS2741 */}
          <UserSearch
            callbacks={{
              onChange: value => setModalState({ filterString: value }),
              selectItem: value => toggleUser(value),
            }}
            endpoint={`/agencies/${campaign.agency_id}/users`}
            selectedIds={selectedUsers.map(user => user.id)}
            value={filterString}
          />
        </Form.Field>

        <Form.Field label="Message" marginTop="large">
          <textarea
            onChange={e => setModalState({ message: e.target.value })}
            style={{ height: 80, resize: 'vertical' }}
            value={message}
          />
        </Form.Field>

        <Form.Field
          label="Selected Recipients"
          labelProps={{ marginBottom: hasSelectedUsers ? 0 : 'medium' }}
          marginTop="large"
        >
          {selectedUsers.map(user => <SelectedRecipient callbacks={{ toggleUser }} user={user} />)}
        </Form.Field>

        {!hasSelectedUsers && (
          <SidebarNoticeBox boxProps={{ marginBottom: 0 }}>
            <SidebarNoticeBox.Title>Recipients Not Added</SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              You haven&apos;t added any recipients.
              Search for a user by their username or ID to add to the recipient list.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        )}

        {provider.id && (
          <Form.Field label="More Options" marginTop={hasSelectedUsers ? 'medium' : 'large'}>
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faEye} />}
              onClick={!isAdCopy ? () => reviewMedia(provider)
                : () => reviewAdCopy(orderItem, provider)}
              size="medium"
            >
              Preview
            </Button>
          </Form.Field>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!hasSelectedUsers}
          icon={<FontAwesomeIcon icon={faEye} />}
          // @ts-expect-error TS2345
          onClick={() => shareProject(shareProjectPayload)}
          size="large"
        >
          Share Project
          {` (${formatEntityCount({ entityCount: selectedUsersCount, entityString: 'Contact' })})`}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ShareProjectModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
